import { memo, Profiler, useCallback } from 'react';
import { useHasPermission } from '../../../providers/AuthProvider';
import { useAdminUser } from '../../../hooks/useAdminUser';
import { UserDashboard } from '../../../admin/components/UserDashboard';
import { EditUserModal } from '../../../admin/components/EditUserModal';
import { LazyCourseEditModal } from '../../../admin/components/LazyComponents';

const AdminUser = memo(() => {
  const hasManageProducts = useHasPermission('manage_products');
  const hasManageCourses = useHasPermission('manage_courses');
  const hasManagePermissions = useHasPermission('manage_permissions');
  const hasManageDevices = useHasPermission('manage_devices') || true; // Temporaneamente abilitato per test
  const hasDev = useHasPermission('dev');
  const hasAnalytics = useHasPermission('analytics');

  // Get admin user data and actions
  const adminUserData = useAdminUser();

  // TESTING PURPOSES ONLY
  const onRenderCallback = useCallback(
    (id: string, phase: string, actualDuration: number) => {
      if (actualDuration > 8) {
        // More aggressive threshold - half a frame
        console.warn(
          `🐌 Slow render detected in ${id}: ${phase} took ${actualDuration}ms`
        );
      }
    },
    []
  );

  return (
    <Profiler id='AdminUser' onRender={onRenderCallback}>
      <Profiler id='UserDashboard' onRender={onRenderCallback}>
        <UserDashboard
          userInfo={adminUserData.userInfo}
          userProducts={adminUserData.userProducts}
          userCourses={adminUserData.userCourses}
          hasManageProducts={hasManageProducts}
          hasManageCourses={hasManageCourses}
          hasManagePermissions={hasManagePermissions}
          hasManageDevices={hasManageDevices}
          hasDev={hasDev}
          hasAnalytics={hasAnalytics}
          onEditUser={adminUserData.openEditUser}
          onDeleteUser={adminUserData.confirmDeleteUser}
          deletingUser={adminUserData.deletingUser}
        />
      </Profiler>

      <EditUserModal
        opened={adminUserData.editUserOpened}
        onClose={adminUserData.closeEditUser}
        user={adminUserData.userInfo}
        saving={adminUserData.savingUser}
        onSave={adminUserData.handleUpdateUser}
      />

      <LazyCourseEditModal
        opened={adminUserData.selectedUserCourse !== undefined}
        onClose={() => adminUserData.setSelectedUserCourse(undefined)}
        course={adminUserData.selectedUserCourse}
        onSave={adminUserData.handleUpdateCourse}
      />
    </Profiler>
  );
});

AdminUser.displayName = 'AdminUser';

export default AdminUser;
